import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Clock } from 'lucide-react';

import client from '../api/client';
import { useCart } from '../context/CartContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import OrderStatusBadge from '../components/order/OrderStatusBadge';
import Badge from '../components/ui/Badge';
import { formatPrice } from '../utils/money';

// Payment settlement now lives on its own field, order.paymentStatus —
// fulfillment status (order.status) is always just "pending" right after
// checkout, so it's not useful here. balance_due means the deposit charge
// succeeded and the order is simply awaiting a later balance payment —
// that's a settled/successful checkout, not still-processing, so it's
// terminal (stop polling) and counts as "paid" for this page's purposes.
const SETTLED_PAYMENT = ['paid', 'balance_due'];
const MAX_POLLS = 10;
const POLL_MS = 3000;

// Reads the order after checkout and polls until the server (via Stripe webhook)
// flips paymentStatus to "paid" (or "balance_due"). The client NEVER marks it paid itself.
export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { refresh } = useCart();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [settled, setSettled] = useState(false);
  const cartRefreshed = useRef(false);

  const fetchOnce = useCallback(async () => {
    const { data } = await client.get(`/order/${orderNumber}`);
    setOrder(data);
    setLoading(false);
    if (SETTLED_PAYMENT.includes(data.paymentStatus) && !cartRefreshed.current) {
      cartRefreshed.current = true;
      refresh(); // server cleared the cart on payment; sync the badge
    }
    return data;
  }, [orderNumber, refresh]);

  useEffect(() => {
    let active = true;
    let tries = 0;
    let timer;

    const poll = async () => {
      try {
        const data = await fetchOnce();
        if (!active) return;
        if (SETTLED_PAYMENT.includes(data.paymentStatus) || tries >= MAX_POLLS) {
          setSettled(true);
          return;
        }
        tries += 1;
        timer = setTimeout(poll, POLL_MS);
      } catch (err) {
        if (!active) return;
        if (err?.response?.status === 404) setNotFound(true);
        setLoading(false);
        setSettled(true);
      }
    };

    poll();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [fetchOnce]);

  if (loading) {
    return <div className="ss-container ss-state"><Spinner label="Loading your order" size={32} /></div>;
  }

  if (notFound || !order) {
    return (
      <section className="ss-container ss-state">
        <h2>Order not found</h2>
        <p style={{ marginTop: 'var(--ss-space-4)' }}>
          <Link className="ss-btn ss-btn--primary" to="/orders">View your orders</Link>
        </p>
      </section>
    );
  }

  const paid = SETTLED_PAYMENT.includes(order.paymentStatus);

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)', maxWidth: 640 }}>
      <div style={{ textAlign: 'center', marginBottom: 'var(--ss-space-5)' }}>
        {paid ? (
          <CheckCircle2 size={56} color="var(--ss-green-deep)" aria-hidden="true" />
        ) : (
          <Clock size={56} color="var(--ss-brown-hover)" aria-hidden="true" />
        )}
        <h1 style={{ marginTop: 'var(--ss-space-3)' }}>
          {paid ? 'Thank you for your order!' : 'Finalizing your payment'}
        </h1>
        <p style={{ color: 'var(--ss-brown-hover)', marginTop: 'var(--ss-space-2)' }}>
          Order <strong>{order.orderNumber}</strong> &nbsp;<OrderStatusBadge status={order.paymentStatus} />
        </p>
      </div>

      {!paid && (
        <Card style={{ marginBottom: 'var(--ss-space-4)' }}>
          <p>
            Your payment is being confirmed. This usually only takes a moment — the
            order updates automatically once the payment settles.
          </p>
          {settled && (
            <p style={{ marginTop: 'var(--ss-space-3)' }}>
              <Button variant="ghost" size="sm" onClick={fetchOnce}>Check again</Button>
            </p>
          )}
        </Card>
      )}

      <Card>
        {(order.items || []).map((i) => (
          <div key={i.product} className="ss-summary-line">
            <span>
              {i.title} × {i.quantity}
              <Badge className={i.fulfillment === 'pickup' ? 'ss-pickup-badge' : 'ss-ship-badge'}>
                {i.fulfillment === 'pickup' ? 'Pickup' : 'Ship'}
              </Badge>
              {i.depositRequired && <Badge className="ss-deposit-badge">{i.depositPercent}% deposit</Badge>}
            </span>
            <span>{formatPrice(i.subTotal ?? i.unitPrice * i.quantity)}</span>
          </div>
        ))}
        <div className="ss-summary-line ss-summary-line--muted" style={{ marginTop: 'var(--ss-space-2)' }}>
          <span>Items</span>
          <span>{formatPrice(order.itemTotal)}</span>
        </div>
        {order.shipping?.method && (
          <div className="ss-summary-line ss-summary-line--muted">
            <span>Shipping ({order.shipping.method})</span>
            <span>{formatPrice(order.shipping.cost)}</span>
          </div>
        )}
        {order.balanceDue > 0 ? (
          <>
            <div className="ss-summary-line ss-summary-line--muted">
              <span>Order total</span>
              <span>{formatPrice(order.total)}</span>
            </div>
            <div className="ss-summary-total">
              <span>Paid now</span>
              <span>{formatPrice(order.amountDueAtCheckout)}</span>
            </div>
            <div className="ss-summary-line ss-summary-line--muted">
              <span>Balance due later</span>
              <span>{formatPrice(order.balanceDue)}</span>
            </div>
          </>
        ) : (
          <div className="ss-summary-total">
            <span>Total</span>
            <span>{formatPrice(order.total)}</span>
          </div>
        )}
      </Card>

      <div style={{ display: 'flex', gap: 'var(--ss-space-3)', marginTop: 'var(--ss-space-5)', justifyContent: 'center' }}>
        <Link className="ss-btn ss-btn--primary" to="/products">Continue shopping</Link>
        <Link className="ss-btn ss-btn--ghost" to="/orders">View all orders</Link>
      </div>
    </section>
  );
}
